import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Salutations } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export type ReLineType =
	| 'salutations.reLineGeneral'
	| 'salutations.reLineCondensed'
	| 'salutations.reLineClients';

@UntilDestroy()
@Component({
	selector: 'app-editing-salutations-relines-dialog',
	templateUrl: './editing-salutations-relines-dialog.component.html',
	standalone: false,
})
export class EditingSalutationsReLinesDialogComponent implements OnInit {
	public height: string;
	public matterId: string;
	public tagValue: string;
	public tagLabel: string;
	public type: ReLineType;
	public salutations: Salutations;

	@Select(MatterClients.get_matterSalutations)
	salutations$: Observable<Salutations>;

	constructor(
		private matterDispatch: MatterDispatch,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			matterId: string;
			type: ReLineType;
			tagValue: string;
			tagLabel: string;
		},
		private dialogRef: MatDialogRef<EditingSalutationsReLinesDialogComponent>,
		private analytics: AnalyticsService,
	) {
		this.matterId = data.matterId;
		this.type = data.type;
		this.tagValue = data.tagValue;
		this.tagLabel = data.tagLabel;
		this.height = '8rem';
	}

	ngOnInit(): void {
		this.salutations$
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(
					(prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
				),
			)
			.subscribe((salutations) => {
				this.salutations = { ...salutations };
				this.tagValue = this.type
					.split('.')
					.reduce((o, i) => this.salutations[i]);
			});
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	onChange($event: string): void {
		this.tagValue = $event;
	}

	save(): void {
		switch (this.type) {
			case 'salutations.reLineGeneral':
				this.matterDispatch.SetSalutationsReLineGeneral(
					this.matterId,
					this.tagValue,
				);
				break;
			case 'salutations.reLineCondensed':
				this.matterDispatch.SetSalutationsReLineCondensed(
					this.matterId,
					this.tagValue,
				);
				break;
			case 'salutations.reLineClients':
				this.matterDispatch.SetSalutationsReLineClients(
					this.matterId,
					this.tagValue,
				);
				break;
		}

		this.analytics.logEvent(
			AnalyticsEvent.CONDENSED_EDITOR_MATTER_SALUTATIONS_RELINES_SAVE_CLICK,
		);
		this.closeModal();
	}

	reset(): void {
		switch (this.type) {
			case 'salutations.reLineGeneral':
				this.matterDispatch.ResetSalutationsReLineGeneral(this.matterId);
				this.tagValue = this.salutations.reLineGeneral;
				break;
			case 'salutations.reLineCondensed':
				this.matterDispatch.ResetSalutationsReLineCondensed(this.matterId);
				this.tagValue = this.salutations.reLineCondensed;
				break;
			case 'salutations.reLineClients':
				this.matterDispatch.ResetSalutationsReLineClients(this.matterId);
				this.tagValue = this.salutations.reLineClients;
				break;
		}

		this.analytics.logEvent(
			AnalyticsEvent.CONDENSED_EDITOR_MATTER_SALUTATIONS_RELINES_RESET_CLICK,
		);
	}
}
